import React, { useState, useEffect, type FunctionComponent } from 'react'
import { useParams } from 'react-router-dom'
import { authenticatedFetchData, API_HOST } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { ConsumerSessionsTable } from '../../common/ConsumerSessionsTable'
import { Button } from '@tomra/design-system-react/button'

type csResult = {
  consumerSession: ConsumerSessionType
  payouts: PayoutType[]
}[]

export const UserConsumerSessions: FunctionComponent = () => {
  const { userId } = useParams()
  const [userCSList, setUserCSList] = useState<ConsumerSessionType[]>([])
  const [requestStatus, setRequestStatus] = useState<string>('loading')
  const [limit, setLimit] = useState<number>(25)
  const canPotentiallyFetchMore = userCSList.length === limit

  const fetchMore = () => setLimit(limit + 25)

  useEffect(() => {
    limit > 25 ? setRequestStatus('loadingMore') : setRequestStatus('loading')

    const f = authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/usercs?userid=${encodeURIComponent(userId || '')}&limit=${limit}`
    )

    f.run().then(
      (result: csResult) => {
        setUserCSList(result.map(cs => cs.consumerSession))
        setRequestStatus('')
      },
      (error: Error) => {
        if (error.name !== 'AbortError') {
          setRequestStatus('failed')
          logError(new Error("Failed to fetch user CS's"), error)
        }
      }
    )

    return () => f.abort()
  }, [userId, limit])

  if (requestStatus === 'loading') {
    return (
      <div className="p-4">
        <div className="loadingSpinner" />
      </div>
    )
  }

  if (requestStatus === 'failed') {
    return (
      <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
        Whoopsie! Failed to fetch consumer sessions for user.
      </div>
    )
  }

  return userCSList.length > 0 ? (
    <>
      <ConsumerSessionsTable consumerSessions={userCSList} />

      <div className="flex justify-center py-6">
        <Button variant="primary" disabled={!canPotentiallyFetchMore} onClick={fetchMore}>
          {requestStatus === 'loadingMore' ? <div className="loadingSpinner" /> : 'More'}
        </Button>
      </div>
    </>
  ) : (
    <div className="p-4">This user has no consumer sessions yet</div>
  )
}
